/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/label-has-for */
import React from 'react';
import _ from 'lodash';
import { Control, Errors } from 'react-redux-form';
import './styles.css';
import Tooltip from '../../Tooltip';
/**
 * The validation handler for the checkboxes
 * @param {*} props The properties to be passed on to the component
 */
const validateCheckboxGroup = ({ model }) => {
  const checkboxSelector = `[name*='${model}'`;
  const checkboxes = document.querySelectorAll(checkboxSelector);
  return Array.from(checkboxes).filter(comp => comp.checked).length !== 0;
};

const getCheckboxProperties = ({ model, options, changeValidity }) => {
  let index = 0;
  let modelName;
  return Object.keys(options).map((key) => {
    const fundingOptionProperty = {
      model: `${model}.${key}`,
      defaultValue: options[key] || false,
      display: options[key],
      inputType: key === 'otherTextValue' ? 'text' : 'checkbox',
      key,
    };
    if (index === 0) {
      fundingOptionProperty.errors = {
        required: () => !validateCheckboxGroup({ model }),
      };
      fundingOptionProperty.errorHandler = {
        messages: {
          required: 'Please select at least one option.',
        },
      };
      modelName = `${model}.${key}`;
    } else {
      fundingOptionProperty.onChange = () => changeValidity(modelName, { required: () => !validateCheckboxGroup({ model }) });
    }
    index += 1;
    return fundingOptionProperty;
  });
};

const validateCheckboxWithAssociatedTextbox = ({ checkboxModel, textboxModel }) => {
  const checkboxSelector = `[name$='${checkboxModel}'`;
  const checkbox = document.querySelectorAll(checkboxSelector);
  if (checkbox.length === 1) {
    if (checkbox[0].checked) {
      const textboxSelector = `[name*='${textboxModel}'`;
      const textbox = document.querySelectorAll(textboxSelector);
      if (textbox.length === 1) {
        return !_.isEmpty(textbox[0].value);
      }
    }
  }
  return true;
};

const handleCheckboxWithAssociatedTextboxChange = ({
  event, initialOnChangeHandler, model, changeFieldValue, changeValidity, checkboxComponent,
}) => {
  initialOnChangeHandler();
  const textBoxSelector = `[name*='${model}'`;
  const textbox = document.querySelectorAll(textBoxSelector);
  if (!event.target.checked) {
    if (textbox.length === 1) {
      textbox[0].attributes.getNamedItem('style').nodeValue = 'display: none;';
    }
    changeFieldValue(model, '');
  } else {
    textbox[0].attributes.getNamedItem('style').nodeValue = '';
  }
  changeValidity(model, {
    required: () => !validateCheckboxWithAssociatedTextbox({ checkboxModel: checkboxComponent.model, textboxModel: model }),
  });
};

const renderCheckboxWithAssociatedTextbox = ({
  checkboxComponent, textboxComponent, changeFieldValue, changeValidity, optionLabels,
}) => {
  const initialOnChangeHandler = checkboxComponent.onChange;
  checkboxComponent.onChange = event => handleCheckboxWithAssociatedTextboxChange({
    event, initialOnChangeHandler, model: textboxComponent.model, changeFieldValue, changeValidity, checkboxComponent,
  });
  textboxComponent.errors = {
    required: () => !validateCheckboxWithAssociatedTextbox({ checkboxModel: checkboxComponent.model, textboxModel: textboxComponent.model }),
  };
  textboxComponent.errorHandler = {
    messages: {
      required: 'Please specify the other mean of funding.',
    },
  };
  return (
    <div styleName="row">
      <div styleName="col little-margin">
        <label styleName="customControl customControl--checkbox">{optionLabels.other}
          <Control.checkbox {...checkboxComponent} />
          <div styleName="customControl__indicator" />
        </label>
      </div>
      <div styleName="col2 text-right little-margin">
        <Control.text
          styleName="suitabilityControl width-400px"
          {...textboxComponent}
          placeholder="Other means of funding this account?"
          style={{ display: !checkboxComponent.defaultValue ? 'none' : 'block' }}
        />
      </div>
      <Errors
        styleName="errorText"
        show
        model={textboxComponent.model}
        {...textboxComponent.errorHandler}
      />
    </div>
  );
};

const renderCheckbox = ({ component, optionLabels }) => (
  <div styleName="row">
    <div styleName="little-margin">
      <label styleName="customControl customControl--checkbox">{optionLabels[component.key]}
        <Control.checkbox {...component} />
        <div styleName="customControl__indicator" />
      </label>
    </div>
  </div>
);

const renderCheckboxes = ({
  checkboxes, optionLabels, changeFieldValue, changeValidity,
}) => checkboxes.filter(component => component.key !== 'other').map((component) => {
  if (component.inputType === 'text') {
    const checkboxComponent = checkboxes.filter(c => c.key === 'other')[0];
    return renderCheckboxWithAssociatedTextbox({
      checkboxComponent, textboxComponent: component, changeFieldValue, changeValidity, optionLabels,
    });
  }
  return renderCheckbox({ component, optionLabels });
});

/**
 * The displayable component for the custom investment objective values
 * @param {*} props The properties to be passed on to the component
 */
export const SuitabilityInvestmentObjectiveDisplayField = (props) => {
  const availableKeysForDisplay = Object.keys(props.options).filter(key => props.options[key]);
  const tooltipValue = !_.isEmpty(props.tooltip) ? (<Tooltip styles={{ display: 'inline-block', marginLeft: '8px' }} text={props.tooltip} />) : ('');
  if (availableKeysForDisplay.length === 0) {
    return (
      <div styleName="suitabilityInfo">
        <div styleName="fieldRow">
          <div><span>{props.label}{tooltipValue}</span></div>
          { props.defaultValue }
        </div>
      </div>
    );
  }
  return (
    <div styleName="suitabilityInfo">
      <div styleName="row">
        <div styleName="col wrap"><span>{props.label}{tooltipValue}</span></div>
        <div styleName="col2 text-right">
          { availableKeysForDisplay.map(key => (<span styleName="colSubHeader singleLine">{ props.optionLabels[key] }</span>))}
        </div>
      </div>
    </div>);
};

/**
  * The edittable wrapper component to be rendered on the form's edit mode
  * @param {*} props The properties to be passed on to the edittable component
  */
export const SuitabilityInvestmentObjectiveEditableField = (props) => {
  const checkboxes = getCheckboxProperties({
    model: props.model,
    options: props.options,
    changeValidity: props.changeValidity,
  });
  return (
    <div styleName="suitabilityInfo">
      <div styleName="row">
        <div>
          <b>{props.label}</b> <span style={{ fontSize: 14 }}>(check all that apply)</span>
        </div>
      </div>
      {renderCheckboxes({
        checkboxes,
        optionLabels: props.optionLabels,
        changeFieldValue: props.changeFieldValue,
        changeValidity: props.changeValidity,
      })}
      <Errors
        styleName="errorText"
        show
        model={checkboxes[0].model}
        {...checkboxes[0].errorHandler}
      />
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/components/Forms/Suitability/SuitabilityInvestmentObjective.js